import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import cx from 'classnames';
import { VenueListSkeleton, Link } from '@resy/ui-kitchen';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import { getResyLocationsByCodeThunk, setSelectedLocationThunk } from '../../../../../../ui-kitchen/src/reducers/resyLocationsSlice';
import {
  setSelectedCoreISODate,
  setSelectedCoreSeats,
} from '../../../../../../ui-kitchen/src/reducers/resyCoreSlice';
import { ISO_DATE_FORMAT } from '../../../../../../ui-kitchen/src/constants/resyCore';
import { getResyCreditEligibleVenues, getConvertedTextData } from './ResyCreditEligibleVenues';
import MultiStepListModal from '../../../../../../ui-kitchen/src/components/MultiStepListModal';
import { handleMktAnalytics } from '../AmexUtils';
import { CTA_EVENT_NAME } from '../constants';
import VenueList from '../../VenueList';
import AmexResySelector from '../AmexResySelector';
import './AmexResyCreditEligibleVenues.scss';

const AmexResyCreditEligibleVenues = ({
  $rootScope,
  $routeParams,
  Analytics,
  selectedCoreISODate,
  selectedLocation,
  selectedCoreSeats,
  selectedCoreTime,
  dispatch,
  content,
  loadNotifyModal,
  matchMedia,
  flowName = 'ALL',
  isResySelector,
  showAuthModal,
  pageLocation,
  pagePartySize,
  pageDate,
  cityName,
  resyLocations,
  userLocation,
  onEligibleVenuesCountChange,
}) => {
  const [eligibleVenues, setEligibleVenues] = useState(null);
  const [venuesLength, setVenuesLength] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorAnnouncementNonce, setErrorAnnouncementNonce] = useState(0);

  const maxCards = 5;

  const {
    section_id: id,
    title,
    titleArr,
    copy,
    error_message: errorMessage,
    copyArr,
    filtering_help: filterHelp,
  } = content;

  const titleFromFlow = titleArr?.find((item) => item.type === flowName)?.copy
    || titleArr?.find((item) => item.type === 'ALL')?.copy
    || '';
  const copyFromFlow = copyArr?.find((item) => item.type === flowName)?.copy
    || copyArr?.find((item) => item.type === 'ALL')?.copy
    || '';
  const sanitizedTitle = sanitizeString(title || getConvertedTextData(cityName, titleFromFlow));
  const sanitizedCopy = sanitizeString(copy || copyFromFlow);
  const sanitizedHelp = sanitizeString(filterHelp);

  useEffect(() => {
    if (pageLocation?.code) return;
    if (selectedLocation?.code === userLocation?.code) return;
    dispatch(setSelectedLocationThunk({ location: userLocation }));
  }, [dispatch, pageLocation?.code, userLocation?.code]);

  useEffect(() => {
    if (!pageLocation?.code) return;
    const location = resyLocations?.all?.find((locationItem) => locationItem.code === pageLocation?.code);
    if (!location?.code || selectedLocation?.code === location.code) return;
    dispatch(setSelectedLocationThunk({ location }));
  }, [dispatch, pageLocation?.code, resyLocations?.all]);

  useEffect(() => {
    setEligibleVenues(null);
    setVenuesLength(null);
    setLoading(true);
    onEligibleVenuesCountChange(0);
    const hasRequiredProps = (pageLocation?.code || selectedLocation?.code)
      && (pageDate || selectedCoreISODate)
      && (pagePartySize || selectedCoreSeats)
      && selectedCoreSeats !== null
      && selectedCoreSeats !== undefined
      && selectedCoreTime;

    if (!hasRequiredProps) return undefined;
    setErrorAnnouncementNonce((n) => n + 1);

    const resolvedPageDate = typeof pageDate === 'string'
      ? pageDate
      : pageDate?.format?.(ISO_DATE_FORMAT);

    const loadEligibleVenues = async () => {
      const locationData = await dispatch(getResyLocationsByCodeThunk(pageLocation?.code || selectedLocation.code));
      if (!locationData?.code) return;

      const venues = await getResyCreditEligibleVenues(
        pagePartySize || selectedCoreSeats,
        resolvedPageDate || selectedCoreISODate,
        selectedCoreTime,
        locationData
      );

      if (resolvedPageDate && resolvedPageDate !== selectedCoreISODate) {
        dispatch(setSelectedCoreISODate(resolvedPageDate));
      }

      if (pagePartySize && pagePartySize !== selectedCoreSeats) {
        dispatch(setSelectedCoreSeats(pagePartySize));
      }

      setVenuesLength(maxCards < venues?.length ? maxCards : venues?.length);
      setEligibleVenues(venues);
      setLoading(false);
    };

    loadEligibleVenues();
    return undefined;
  }, [
    dispatch,
    selectedLocation?.code,
    pagePartySize || selectedCoreSeats,
    pageDate || selectedCoreISODate,
    selectedCoreTime,
    pageLocation?.code,
    pagePartySize,
    pageDate,
    onEligibleVenuesCountChange,
  ]);

  useEffect(() => {
    if (loading) return;
    onEligibleVenuesCountChange(venuesLength || 0);
  }, [loading, venuesLength, onEligibleVenuesCountChange]);

  const handleLinkClick = () => {
    const extraParams = {
      action_name: 'Resy.com Click',
    };
    handleMktAnalytics(Analytics.event, CTA_EVENT_NAME, extraParams);
  };

  const renderErrorMessage = () => {
    // Handle both string format (legacy) and object format { text, link }
    if (!errorMessage) return null;
    const messageConfig = typeof errorMessage === 'string'
      ? { text: errorMessage, link: null }
      : errorMessage;

    const { text, link } = messageConfig;
    // Toggle a single invisible ZWSP so screen readers detect a text change and re-announce.
    const announcementSuffix = errorAnnouncementNonce % 2 === 1 ? '\u200B' : '';
    const sanitizedErrorMessageText = `${sanitizeString(text)}${announcementSuffix}`;
    const sanitizedLinkText = link?.text
      ? `${sanitizeString(link.text)}${announcementSuffix}`
      : '';

    return (
      <p
        className="AmexResyCreditEligibleVenues__notification-error"
      >
        <span
          dangerouslySetInnerHTML={{ __html: sanitizedErrorMessageText }} // eslint-disable-line react/no-danger
        />
        {link && (
          <Link href={link.href} target="_blank" rel="noopener noreferrer" onClick={handleLinkClick}>
            {sanitizedLinkText}
          </Link>
        )}
      </p>
    );
  };

  if (!loading && eligibleVenues && eligibleVenues?.length === 0 && !isResySelector) {
    return null;
  }

  const renderTitleSection = () => (
    <>
      <h2
        id={id}
        className="AmexResyCreditEligibleVenues__title"
        dangerouslySetInnerHTML={{ __html: sanitizedTitle }}
      />
      <p
        className="AmexResyCreditEligibleVenues__copy"
        dangerouslySetInnerHTML={{ __html: sanitizedCopy }}
      />
    </>
  );

  const renderVenueGrid = () => (
    <div
      className={cx({
        'AmexResyCreditEligibleVenues__small-list': eligibleVenues?.length <= maxCards,
      })}
    >
      <VenueList
        $rootScope={$rootScope}
        hasNoResults={venuesLength === 0}
        restaurants={eligibleVenues}
        selectedDate={selectedCoreISODate}
        selectedSeats={selectedCoreSeats}
        selectedTime={selectedCoreTime}
        loadNotifyModal={loadNotifyModal}
        locationCode={selectedLocation?.code || 'ny'}
        locationSlug={selectedLocation?.url_slug || 'new-york-ny'}
        event={Analytics.event}
        isMobile={!matchMedia.lg}
        showSeeMore
        maxCards={maxCards}
        matchMedia={matchMedia}
        showCarouselButtons={venuesLength > maxCards}
        isResyCreditEligible
        showAuthModal={showAuthModal}
        isAmexMktgResyCreditEligible
      />
    </div>
  );
  return (

    <section
      className="AmexResyCreditEligibleVenues"
    >
      <div className="container">
        {!isResySelector && !loading && renderTitleSection()}
        {isResySelector && renderTitleSection()}
        {isResySelector && (
          <aside
            className={cx('AmexResyCreditEligibleVenues__selectors', { 'AmexResyCreditEligibleVenues__selectors--mobile': !matchMedia.lg })}
            aria-label="Eligible venues filters"
            aria-describedby="filtersHelp"
          >
            {filterHelp && (<p id="filtersHelp" className="sr-only">{sanitizedHelp}</p>)}
            <AmexResySelector
              $rootScope={$rootScope}
              $routeParams={$routeParams}
              Analytics={Analytics}
              selectedCoreISODate={selectedCoreISODate}
              selectedCoreSeats={selectedCoreSeats}
              selectedCoreTime={selectedCoreTime}
              dispatch={dispatch}
              hideWineLocations
            />
          </aside>
        )}
      </div>
      {(loading && (!eligibleVenues || eligibleVenues?.length === 0)) && (<VenueListSkeleton />)}
      {(!loading && (eligibleVenues?.length > 0)) && renderVenueGrid()}
      <div role="status" aria-atomic="true">
        {(!loading && (!eligibleVenues || eligibleVenues?.length === 0)) && renderErrorMessage()}
      </div>
      <MultiStepListModal Analytics={Analytics} />
    </section>

  );
};

AmexResyCreditEligibleVenues.propTypes = {
  $rootScope: PropTypes.shape({
    $broadcast: PropTypes.func,
    $digest: PropTypes.func,
  }).isRequired,
  $routeParams: PropTypes.shape({}).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  selectedCoreISODate: PropTypes.string.isRequired,
  selectedLocation: PropTypes.shape({
    code: PropTypes.string,
    url_slug: PropTypes.string,
  }),
  selectedCoreSeats: PropTypes.number.isRequired,
  selectedCoreTime: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  content: PropTypes.shape({
    section_id: PropTypes.string,
    title: PropTypes.string,
    titleArr: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string,
        copy: PropTypes.string,
      })
    ),
    copy: PropTypes.string,
    error_message: PropTypes.string.isRequired,
    filtering_help: PropTypes.string,
    copyArr: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string,
        copy: PropTypes.string,
      })
    ).isRequired,
  }).isRequired,
  loadNotifyModal: PropTypes.func.isRequired,
  matchMedia: PropTypes.shape({
    lg: PropTypes.bool,
  }).isRequired,
  flowName: PropTypes.string,
  isResySelector: PropTypes.bool,
  showAuthModal: PropTypes.func.isRequired,
  pageLocation: PropTypes.shape({
    code: PropTypes.string,
    url_slug: PropTypes.string,
  }),
  cityName: PropTypes.string,
  pagePartySize: PropTypes.number,
  pageDate: PropTypes.string,
  userLocation: PropTypes.shape({
    code: PropTypes.string,
    url_slug: PropTypes.string,
  }),
  resyLocations: PropTypes.shape({
    all: PropTypes.arrayOf(
      PropTypes.shape({
        code: PropTypes.string,
        url_slug: PropTypes.string,
      })
    ),
  }).isRequired,
  onEligibleVenuesCountChange: PropTypes.func,
};

AmexResyCreditEligibleVenues.defaultProps = {
  selectedLocation: null,
  flowName: null,
  isResySelector: false,
  pageLocation: null,
  cityName: null,
  pagePartySize: null,
  pageDate: null,
  userLocation: null,
  onEligibleVenuesCountChange: () => {},
};

const mapStateToProps = (state, ownProps) => {
  const {
    selectedCoreISODate, selectedCoreSeats, selectedCoreTime,
  } = state.resyCore;
  let userLocation = null;
  if (!ownProps?.pageLocation?.code) {
    if (state.ipdata?.isNonUS) {
      userLocation = state.resyLocations?.nav?.find((location) => location.code === 'ny') || null;
    } else {
      userLocation = state.resyLocations?.all?.find((location) => location.show_on_web === 1)
        || state.resyLocations?.all?.[0]
        || null;
    }
  }

  return {
    selectedLocation: state.resyLocations?.selected || null,
    selectedCoreISODate,
    selectedCoreSeats,
    selectedCoreTime,
    userLocation,
    resyLocations: state.resyLocations,
  };
};

AmexResyCreditEligibleVenues.displayName = 'AmexResyCreditEligibleVenues';
export { AmexResyCreditEligibleVenues as ComponentAmexResyCreditEligibleVenuesUnderTest, mapStateToProps };
export default connect(mapStateToProps)(AmexResyCreditEligibleVenues);
